import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Stack,
  Typography,
  Paper,
  Chip,
  IconButton,
  Alert,
  Divider,
  Badge,
  Tooltip,
  LinearProgress,
  Card,
  CardContent,
  Button,
  useTheme
} from '@mui/material';
import {
  Print,
  Download,
  Refresh as RefreshIcon,
  ViewList,
  Assessment,
  Hotel,
  Person,
  EventAvailable,
  EventBusy,
  TrendingUp,
  Warning,
  DateRange,
  Search,
  FilterList,
  Settings
} from '@mui/icons-material';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import { useNavigate } from 'react-router-dom';

import { getApi } from 'views/services/api';

// Componentes existentes
import Toolbar from './components/Toolbar';
import StatsBar from './components/StatsBar';
import Legend from './components/Legend';
import RoomsSidebar from './components/RoomsSidebar';
import TimelinePane from './components/TimelinePane';

// Hooks y utilidades
import useCalendarHeaders from './hooks/useCalendarHeaders';
import { asRooms, asReservations, overlaps } from './utils/dateUtils';
import { getReservationStatus } from './utils/reservationStatus';
import { ROW_HEIGHT, LEFT_COL_WIDTH, CELL_WIDTH } from './constants/layout';

// Colores profesionales PMS - Enterprise Grade
const PMS_COLORS = {
  // Paleta principal - Modern Enterprise Blue
  primary: {
    main: '#0F172A', // Slate 900
    light: '#334155', // Slate 700
    dark: '#020617', // Slate 950
    contrast: '#FFFFFF'
  },
  secondary: {
    main: '#6366F1', // Indigo 500
    light: '#818CF8', // Indigo 400
    dark: '#4F46E5' // Indigo 600
  },

  // Estados de habitaciones - High Visibility & Accessibility
  rooms: {
    occupied: '#EF4444', // Red 500
    vacant_dirty: '#F59E0B', // Amber 500
    vacant_clean: '#10B981', // Emerald 500
    out_of_order: '#1F2937', // Gray 800 - distinct from disabled
    maintenance: '#8B5CF6', // Violet 500
    blocked: '#DC2626', // Red 600
    inspected: '#06B6D4' // Cyan 500
  },

  // Estados de reservas - Clear Distinction
  reservations: {
    confirmed: '#059669', // Emerald 600 - Rich Green
    tentative: '#D97706', // Amber 600 - Dark Warning
    cancelled: '#9CA3AF', // Gray 400 - Deleted look
    no_show: '#4B5563', // Gray 600 - Darker gray
    checked_in: '#2563EB', // Blue 600 - Standard Active
    checked_out: '#7C3AED', // Violet 600 - Distinct from checked in
    group: '#DB2777', // Pink 600 - Standout for groups
    walk_in: '#0D9488' // Teal 600 - Special
  },

  // UI y estructura - Clean & Minimalist
  ui: {
    background: '#F1F5F9', // Slate 100 - Very light gray
    surface: '#FFFFFF', // Pure White
    border: '#E2E8F0', // Slate 200 - Subtle
    divider: '#CBD5E1', // Slate 300
    sidebar: '#F8FAFC', // Slate 50 - Slightly off-white
    header: '#FFFFFF', // White
    hover: '#F1F5F9' // Hover state
  },

  // Texto y contenido
  text: {
    primary: '#0F172A', // Slate 900
    secondary: '#64748B', // Slate 500
    disabled: '#94A3B8', // Slate 400
    inverse: '#FFFFFF'
  },

  // Estados de ocupación
  occupancy: {
    high: '#B91C1C', // Red 700
    medium: '#D97706', // Amber 600
    low: '#059669', // Emerald 600
    full: '#7F1D1D' // Red 900
  },

  // Alertas mejoradas
  alerts: {
    error: '#FEF2F2', // Backgrounds
    warning: '#FFFBEB',
    success: '#ECFDF5',
    info: '#EFF6FF',
    errorText: '#991B1B',
    warningText: '#92400E',
    successText: '#065F46',
    infoText: '#1E40AF'
  }
};

dayjs.extend(isBetween);
dayjs.extend(weekOfYear);

export default function PMSRoomsCalendar() {
  const navigate = useNavigate();
  const theme = useTheme();

  // Datos del hotel
  const hotelData = (() => {
    try {
      return JSON.parse(localStorage.getItem('hotelData') || '{}');
    } catch {
      return {};
    }
  })();
  const hotelId = hotelData?.hotelId || null;

  // Estados principales
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rooms, setRooms] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Configuración de vista - Rack mensual por defecto (estándar PMS maduro)
  const [viewMode, setViewMode] = useState('month');
  const [cellWidth, setCellWidth] = useState(CELL_WIDTH);
  const [rangeStart, setRangeStart] = useState(dayjs().startOf('month').toDate());
  const [rangeEnd, setRangeEnd] = useState(dayjs().endOf('month').toDate());

  // Filtros avanzados PMS
  const [filterRoomType, setFilterRoomType] = useState('all');
  const [filterFloor, setFilterFloor] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterRoomCondition, setFilterRoomCondition] = useState('all');
  const [filterReservationStatus, setFilterReservationStatus] = useState('all');
  const [searchGuest, setSearchGuest] = useState('');
  const [showOutOfOrder, setShowOutOfOrder] = useState(true);
  const [showMaintenanceRooms, setShowMaintenanceRooms] = useState(true);

  // Estados adicionales PMS
  const [alerts, setAlerts] = useState([]);
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Cálculos optimizados del timeline
  const totalDays = useMemo(() => {
    if (viewMode === 'hourly') return 1;
    return Math.max(1, dayjs(rangeEnd).diff(rangeStart, 'day') + 1);
  }, [viewMode, rangeStart, rangeEnd]);

  const timelineWidth = useMemo(() => {
    if (viewMode === 'hourly') return 24 * cellWidth;
    return totalDays * cellWidth;
  }, [viewMode, totalDays, cellWidth]);

  // Clasifica una reserva en una de las categorías del filtro de pills
  const matchesStatusFilter = (rv) => {
    if (filterReservationStatus === 'all') return true;
    const s = (rv.status || '').toLowerCase();
    switch (filterReservationStatus) {
      case 'confirmed':
        return !rv.checkedIn && s !== 'cancelled' && s !== 'no-show' && s !== 'no_show' && s !== 'pending' && s !== 'tentative' && s !== 'waitlist';
      case 'checked_in':
        return rv.checkedIn && !rv.checkedOut;
      case 'tentative':
        return s === 'pending' || s === 'tentative' || s === 'waitlist';
      case 'group':
        return !!rv.groupCode;
      case 'walk_in':
        return (rv.source || '').toLowerCase().includes('walk');
      case 'cancelled':
        return s === 'cancelled';
      case 'no_show':
        return s === 'no-show' || s === 'no_show';
      default:
        return true;
    }
  };

  // Mapeo avanzado de reservas por habitación
  const reservationsByRoom = useMemo(() => {
    const byRoom = new Map();

    reservations.forEach((rv) => {
      const checkIn = dayjs(rv.checkInDate || rv.checkinDate);
      const checkOut = dayjs(rv.checkOutDate || rv.checkoutDate);

      if (!checkIn.isValid()) return;

      const start = checkIn.toDate();
      const end = checkOut.isValid() ? checkOut.toDate() : checkIn.add(1, 'day').toDate();

      // Verificar solapamiento con rango visible
      if (!overlaps(start, end, rangeStart, rangeEnd)) return;

      // Filtro por estado del pill activo
      if (!matchesStatusFilter(rv)) return;

      const status = getReservationStatus(rv);
      const guest =
        rv?.customers?.[0]?.name ||
        (rv?.customerDetails
          ? `${rv.customerDetails.firstName || ''} ${rv.customerDetails.lastName || ''}`.trim()
          : rv.fullName || rv.name || 'Guest');

      const roomNo = String(rv.roomNo ?? rv.room?.roomNo ?? rv.room_number ?? rv.room?.number ?? '__unassigned__');

      if (!byRoom.has(roomNo)) byRoom.set(roomNo, []);

      byRoom.get(roomNo).push({
        id: String(rv._id || rv.id || `${roomNo}-${checkIn.valueOf()}`),
        start,
        end,
        checkIn,
        checkOut,
        color: PMS_COLORS.reservations[status.type] || status.color,
        statusLabel: status.label,
        title: guest,
        status: rv.status || 'confirmed',
        price: rv.totalAmount || rv.amount || null,
        nights: Math.max(1, checkOut.diff(checkIn, 'day')),
        adults: rv.adults || rv.customerDetails?.adults || 2,
        children: rv.children || rv.customerDetails?.children || 0,
        checkedIn: rv.checkedIn || false,
        checkedOut: rv.checkedOut || false,
        notes: rv.notes || rv.specialRequests || '',
        source: rv.source || rv.bookingSource || 'Direct',
        confirmationNumber: rv.confirmationNumber || rv.bookingNumber || '',
        roomCondition: rv.roomCondition || 'clean',
        vip: rv.vip || false,
        groupCode: rv.groupCode || null,
        rateCode: rv.rateCode || 'BAR',
        marketSegment: rv.marketSegment || 'Leisure',
        raw: rv
      });
    });

    // Ordenar reservas por fecha de inicio
    byRoom.forEach((arr) => arr.sort((a, b) => a.start - b.start));
    return byRoom;
  }, [reservations, rangeStart, rangeEnd, filterReservationStatus]);

  // Filtrado avanzado de habitaciones
  const filteredRooms = useMemo(() => {
    return rooms.filter((room) => {
      // Filtro por tipo de habitación
      if (filterRoomType !== 'all' && room.roomType !== filterRoomType) return false;

      // Filtro por piso
      if (filterFloor !== 'all') {
        const floor = Math.floor(parseInt(room.roomNo, 10) / 100);
        if (floor !== parseInt(filterFloor, 10)) return false;
      }

      // Filtro por condición de habitación
      if (filterRoomCondition !== 'all' && room.condition !== filterRoomCondition) return false;

      // Filtro por estado de habitación
      if (filterStatus !== 'all' && room.status !== filterStatus) return false;

      // Filtro por huésped
      if (searchGuest) {
        const resv = reservationsByRoom.get(String(room.roomNo)) || [];
        if (!resv.some((r) => r.title.toLowerCase().includes(searchGuest.toLowerCase()))) return false;
      }

      // Filtros especiales
      if (!showOutOfOrder && room.status === 'out_of_order') return false;
      if (!showMaintenanceRooms && room.status === 'maintenance') return false;

      return true;
    });
  }, [
    rooms,
    filterRoomType,
    filterFloor,
    filterRoomCondition,
    filterStatus,
    searchGuest,
    reservationsByRoom,
    showOutOfOrder,
    showMaintenanceRooms
  ]);

  // Estadísticas avanzadas PMS
  const statistics = useMemo(() => {
    const today = dayjs();

    // Reservas de hoy
    const todayReservations = reservations.filter((r) => {
      const ci = dayjs(r.checkInDate || r.checkinDate);
      const co = dayjs(r.checkOutDate || r.checkoutDate);
      return today.isBetween(ci, co, 'day', '[]');
    });

    // Arribos y salidas
    const arrivals = reservations.filter((r) => dayjs(r.checkInDate || r.checkinDate).isSame(today, 'day'));
    const departures = reservations.filter((r) => dayjs(r.checkOutDate || r.checkoutDate).isSame(today, 'day'));

    // Habitaciones por estado
    const roomsByStatus = filteredRooms.reduce((acc, room) => {
      const status = room.status || 'vacant_clean';
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});

    // Cálculos de ocupación
    const totalRooms = filteredRooms.length;
    const occupiedRooms = todayReservations.length;
    const availableRooms = totalRooms - occupiedRooms;
    const occupancyRate = totalRooms > 0 ? Math.round((occupiedRooms / totalRooms) * 100) : 0;

    // In-house guests
    const inHouse = todayReservations.filter((r) => r.checkedIn && !r.checkedOut).length;

    // Revenue
    const todayRevenue = todayReservations.reduce((sum, r) => sum + (r.totalAmount || r.amount || 0), 0);
    const adr = occupiedRooms > 0 ? todayRevenue / occupiedRooms : 0;

    return {
      totalRooms,
      occupiedRooms,
      availableRooms,
      occupancyRate,
      arrivals: arrivals.length,
      departures: departures.length,
      inHouse,
      roomsByStatus,
      todayRevenue,
      adr,
      revPAR: todayRevenue / totalRooms || 0,
      walkIns: arrivals.filter((r) => r.source === 'Walk-in').length,
      noShows: arrivals.filter((r) => r.status === 'no_show').length,
      earlyDepartures: departures.filter((r) => r.checkedOut && dayjs(r.checkOutDate).isBefore(today)).length
    };
  }, [reservations, filteredRooms]);

  // Contadores por estado — alimentan los filter pills del Toolbar
  const statusCounts = useMemo(() => {
    const counts = {
      all: reservations.length,
      confirmed: 0,
      checked_in: 0,
      tentative: 0,
      group: 0,
      walk_in: 0,
      cancelled: 0,
      no_show: 0
    };
    reservations.forEach((r) => {
      const s = (r.status || '').toLowerCase();
      if (s === 'cancelled') counts.cancelled++;
      else if (s === 'no-show' || s === 'no_show') counts.no_show++;
      else if (r.checkedIn && !r.checkedOut) counts.checked_in++;
      else if (s === 'pending' || s === 'tentative' || s === 'waitlist') counts.tentative++;
      else counts.confirmed++;

      if (r.groupCode) counts.group++;
      if ((r.source || '').toLowerCase().includes('walk')) counts.walk_in++;
    });
    return counts;
  }, [reservations]);

  // Sistema de alertas PMS
  const generateAlerts = useCallback(() => {
    const newAlerts = [];
    const today = dayjs();

    // Alert: Alta ocupación
    if (statistics.occupancyRate > 95) {
      newAlerts.push({
        id: 'high_occupancy',
        type: 'warning',
        title: 'Ocupación Crítica',
        message: `${statistics.occupancyRate}%`,
        icon: Warning
      });
    }

    // Alert: Habitaciones fuera de servicio
    const oooRooms = statistics.roomsByStatus.out_of_order || 0;
    if (oooRooms > 0) {
      newAlerts.push({
        id: 'ooo_rooms',
        type: 'error',
        title: 'Bloqueos',
        message: `${oooRooms} OOO`,
        icon: EventBusy
      });
    }

    // Alert: No-shows
    if (statistics.noShows > 0) {
      newAlerts.push({
        id: 'no_shows',
        type: 'warning',
        title: 'No-Shows',
        message: `${statistics.noShows}`,
        icon: Person
      });
    }

    setAlerts(newAlerts);
  }, [statistics]);

  // Función de fetch mejorada
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      if (!hotelId) {
        setError('No hay hotel seleccionado en el sistema');
        return;
      }

      // Fetch paralelo para mejor performance
      const [roomsRes, resvRes] = await Promise.allSettled([
        getApi(`api/room/viewallrooms/${hotelId}`),
        getApi(`api/reservation/viewpendingandactivereservations/${hotelId}`).catch(() =>
          getApi(`api/reservation/viewallactivereservations/${hotelId}`)
        )
      ]);

      if (roomsRes.status === 'fulfilled') {
        const roomsArr = asRooms(roomsRes.value).sort((a, b) => (parseInt(a.roomNo, 10) || 0) - (parseInt(b.roomNo, 10) || 0));
        setRooms(roomsArr);
      } else {
        throw new Error('Error al cargar habitaciones');
      }

      if (resvRes.status === 'fulfilled') {
        setReservations(asReservations(resvRes.value));
      } else {
        throw new Error('Error al cargar reservas');
      }

      setLastUpdated(new Date());
    } catch (e) {
      console.error('PMS Calendar Error:', e);
      setError(`Error del sistema PMS: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [hotelId]);

  // Auto-refresh cada 5 minutos (estándar PMS)
  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(fetchData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchData, autoRefresh]);

  // Generar alertas cuando cambien las estadísticas
  useEffect(() => {
    if (!loading && statistics.totalRooms > 0) {
      generateAlerts();
    }
  }, [statistics, loading, generateAlerts]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Funciones de navegación mejoradas
  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    const today = dayjs();

    switch (mode) {
      case 'hourly':
        setRangeStart(today.startOf('day').toDate());
        setRangeEnd(today.endOf('day').toDate());
        setCellWidth(80);
        break;
      case 'day':
        setRangeStart(today.startOf('day').toDate());
        setRangeEnd(today.add(3, 'day').endOf('day').toDate());
        setCellWidth(Math.round(CELL_WIDTH * 1.4));
        break;
      case 'week':
        setRangeStart(today.startOf('week').toDate());
        setRangeEnd(today.endOf('week').toDate());
        setCellWidth(Math.round(CELL_WIDTH * 1.6));
        break;
      case 'twoWeeks':
        setRangeStart(today.startOf('week').toDate());
        setRangeEnd(today.startOf('week').add(2, 'week').toDate());
        setCellWidth(Math.round(CELL_WIDTH * 1.1));
        break;
      case 'month':
        setRangeStart(today.startOf('month').toDate());
        setRangeEnd(today.endOf('month').toDate());
        setCellWidth(CELL_WIDTH);
        break;
      default:
        break;
    }
  };

  // Navegación temporal
  const goToday = () => handleViewModeChange(viewMode);
  const goPrev = () => {
    const s = dayjs(rangeStart);
    switch (viewMode) {
      case 'hourly':
        setRangeStart(s.subtract(1, 'day').toDate());
        setRangeEnd(s.subtract(1, 'day').endOf('day').toDate());
        break;
      case 'day':
        setRangeStart(s.subtract(3, 'day').toDate());
        setRangeEnd(dayjs(rangeEnd).subtract(3, 'day').toDate());
        break;
      case 'week':
        setRangeStart(s.subtract(1, 'week').toDate());
        setRangeEnd(s.subtract(1, 'week').endOf('week').toDate());
        break;
      case 'twoWeeks':
        setRangeStart(s.subtract(2, 'week').toDate());
        setRangeEnd(s.subtract(2, 'week').add(2, 'week').toDate());
        break;
      case 'month':
        setRangeStart(s.subtract(1, 'month').toDate());
        setRangeEnd(s.subtract(1, 'month').endOf('month').toDate());
        break;
      default:
        break;
    }
  };

  const goNext = () => {
    const s = dayjs(rangeStart);
    switch (viewMode) {
      case 'hourly':
        setRangeStart(s.add(1, 'day').toDate());
        setRangeEnd(s.add(1, 'day').endOf('day').toDate());
        break;
      case 'day':
        setRangeStart(s.add(3, 'day').toDate());
        setRangeEnd(dayjs(rangeEnd).add(3, 'day').toDate());
        break;
      case 'week':
        setRangeStart(s.add(1, 'week').toDate());
        setRangeEnd(s.add(1, 'week').endOf('week').toDate());
        break;
      case 'twoWeeks':
        setRangeStart(s.add(2, 'week').toDate());
        setRangeEnd(s.add(4, 'week').toDate());
        break;
      case 'month':
        setRangeStart(s.add(1, 'month').startOf('month').toDate());
        setRangeEnd(s.add(1, 'month').endOf('month').toDate());
        break;
      default:
        break;
    }
  };

  // Zoom controls
  const zoomIn = () => setCellWidth(Math.min(200, Math.round(cellWidth * 1.25)));
  const zoomOut = () => setCellWidth(Math.max(30, Math.round(cellWidth * 0.8)));

  // Headers del calendario
  const headers = useCalendarHeaders({
    viewMode,
    rangeStart,
    rangeEnd,
    cellWidth,
    timelineWidth
  });

  // Referencias para scroll sincronizado
  const rightXRef = useRef(null);
  const leftListRef = useRef(null);
  const rightListRef = useRef(null);
  const syncingRef = useRef(false);

  // Auto-scroll a la hora actual
  useEffect(() => {
    if (viewMode !== 'hourly') return;

    const scrollToCurrentHour = () => {
      if (!rightXRef.current) return;
      const now = dayjs();
      const isToday = dayjs(rangeStart).isSame(now, 'day');
      const targetHour = isToday ? now.hour() : 8;
      const containerWidth = window.innerWidth - LEFT_COL_WIDTH;
      const targetPosition = targetHour * cellWidth;
      const centeredPosition = Math.max(0, targetPosition - containerWidth / 2);

      rightXRef.current.scrollTo({
        left: centeredPosition,
        behavior: loading ? 'auto' : 'smooth'
      });
    };

    if (loading) {
      const timeoutId = setTimeout(scrollToCurrentHour, 150);
      return () => clearTimeout(timeoutId);
    } else {
      let frameId1 = requestAnimationFrame(() => {
        let frameId2 = requestAnimationFrame(scrollToCurrentHour);
        return () => cancelAnimationFrame(frameId2);
      });
      return () => cancelAnimationFrame(frameId1);
    }
  }, [viewMode, rangeStart, cellWidth, loading, filteredRooms.length]);

  const jumpToCurrentHour = useCallback(() => {
    if (!rightXRef.current) return;
    const now = dayjs();
    const exactPosition = (now.hour() + now.minute() / 60) * cellWidth;
    const containerWidth = window.innerWidth - LEFT_COL_WIDTH;
    const centeredPosition = Math.max(0, exactPosition - containerWidth / 2);

    rightXRef.current.scrollTo({
      left: centeredPosition,
      behavior: 'smooth'
    });
  }, [cellWidth]);

  // Sincronización de scroll vertical
  const syncFromRight = (offset) => {
    if (!leftListRef.current || syncingRef.current) return;
    syncingRef.current = true;
    leftListRef.current.scrollTo(offset);
    requestAnimationFrame(() => {
      syncingRef.current = false;
    });
  };

  const syncFromLeft = (offset) => {
    if (!rightListRef.current || syncingRef.current) return;
    syncingRef.current = true;
    rightListRef.current.scrollTo(offset);
    requestAnimationFrame(() => {
      syncingRef.current = false;
    });
  };

  // Render Loader
  if (loading) {
    return (
      <Box
        sx={{
          height: '100vh',
          width: '100vw',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: PMS_COLORS.ui.background
        }}
      >
        <Stack alignItems="center" spacing={2}>
          <LinearProgress sx={{ width: 200, height: 4, borderRadius: 2 }} />
          <Typography variant="caption" color="textSecondary">
            Cargando...
          </Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        backgroundColor: PMS_COLORS.ui.background,
        overflow: 'hidden'
      }}
    >
      {/* 1. Top Bar - Dashboard Style */}
      <Paper
        elevation={0}
        square
        sx={{
          py: 1.5,
          px: 3,
          backgroundColor: PMS_COLORS.ui.header,
          borderBottom: `1px solid ${PMS_COLORS.ui.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          zIndex: 10
        }}
      >
        {/* Left: Branding & Overview */}
        <Stack direction="row" alignItems="center" spacing={3}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '8px',
                backgroundColor: PMS_COLORS.primary.main,
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Hotel fontSize="small" />
            </Box>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.1, color: PMS_COLORS.text.primary }}>
                {hotelData?.hotelName || 'PMS Dashboard'}
              </Typography>
              <Typography variant="caption" sx={{ color: PMS_COLORS.text.secondary }}>
                {dayjs().format('DD MMM YYYY • dddd')}
              </Typography>
            </Box>
          </Box>

          <Divider orientation="vertical" flexItem sx={{ height: 24, alignSelf: 'center' }} />

          <Stack direction="row" spacing={1}>
            <Chip
              icon={<TrendingUp sx={{ fontSize: 16 }} />}
              label={`${statistics.occupancyRate}% Ocupación`}
              size="small"
              sx={{
                fontWeight: 600,
                backgroundColor: statistics.occupancyRate > 80 ? PMS_COLORS.occupancy.high : PMS_COLORS.occupancy.low,
                color: '#fff',
                '& .MuiChip-icon': { color: 'inherit' }
              }}
            />
            <Chip
              label={`ADR $${statistics.adr.toFixed(0)}`}
              size="small"
              variant="outlined"
              sx={{ borderColor: PMS_COLORS.ui.border, fontWeight: 500 }}
            />
          </Stack>
        </Stack>

        {/* Right: Actions */}
        <Stack direction="row" alignItems="center" spacing={1}>
          <Button
            startIcon={<RefreshIcon />}
            variant="text"
            size="small"
            onClick={fetchData}
            disabled={loading}
            sx={{ color: PMS_COLORS.text.primary }}
          >
            Actualizar
          </Button>
          <Divider orientation="vertical" flexItem sx={{ height: 20, alignSelf: 'center', mx: 1 }} />
          <IconButton size="small">
            <Search fontSize="small" />
          </IconButton>
          <IconButton size="small">
            <FilterList fontSize="small" />
          </IconButton>
          <IconButton size="small">
            <Settings fontSize="small" />
          </IconButton>
        </Stack>
      </Paper>

      {/* 2. Controls & Legend Bar */}
      <Box
        sx={{
          px: 3,
          py: 1,
          backgroundColor: PMS_COLORS.ui.surface,
          borderBottom: `1px solid ${PMS_COLORS.ui.border}`
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%' }}>
          {/* Toolbar Area */}
          <Box sx={{ flex: 1 }}>
            <Toolbar
              viewMode={viewMode}
              onChangeView={handleViewModeChange}
              goPrev={goPrev}
              goNext={goNext}
              goToday={goToday}
              zoomIn={zoomIn}
              zoomOut={zoomOut}
              rangeStart={rangeStart}
              rangeEnd={rangeEnd}
              filterReservationStatus={filterReservationStatus}
              onChangeReservationStatus={setFilterReservationStatus}
              searchGuest={searchGuest}
              onChangeSearch={setSearchGuest}
              statusCounts={statusCounts}
              pmsColors={PMS_COLORS}
            />
          </Box>
        </Stack>
      </Box>

      {/* 3. Main Workspace - Grid */}
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden', position: 'relative' }}>
        {/* Left Sidebar */}
        <RoomsSidebar
          ref={leftListRef}
          rooms={filteredRooms}
          reservationsByRoom={reservationsByRoom}
          height={window.innerHeight - 180} // Approx offset for new headers
          rowHeight={ROW_HEIGHT}
          onSyncScroll={syncFromLeft}
          pmsColors={PMS_COLORS}
          viewMode={viewMode}
        />

        {/* Right Timeline */}
        <TimelinePane
          refList={rightListRef}
          xScrollRef={rightXRef}
          rooms={filteredRooms}
          reservationsByRoom={reservationsByRoom}
          headers={headers}
          viewMode={viewMode}
          cellWidth={cellWidth}
          timelineWidth={timelineWidth}
          height={window.innerHeight - 180}
          rowHeight={ROW_HEIGHT}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          totalDays={totalDays}
          pmsColors={PMS_COLORS}
          onOpenReservation={(id) => navigate(`/dashboard/reservation/view/${id}`)}
          onSyncScroll={syncFromRight}
          showTimeIndicator={viewMode === 'hourly'}
          jumpStart={() => rightXRef.current && (rightXRef.current.scrollLeft = 0)}
          jumpNow={jumpToCurrentHour}
          jumpEnd={() => rightXRef.current && (rightXRef.current.scrollLeft = timelineWidth)}
        />
      </Box>

      {/* 4. Footer / Stats Footer */}
      <Paper
        square
        elevation={4}
        sx={{
          mt: 'auto',
          p: 1,
          px: 3,
          backgroundColor: PMS_COLORS.ui.surface,
          borderTop: `1px solid ${PMS_COLORS.ui.border}`,
          zIndex: 20
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
          <Legend pmsColors={PMS_COLORS} />
          <StatsBar statistics={statistics} pmsColors={PMS_COLORS} showAdvancedMetrics={false} />
          {alerts.length > 0 && (
            <Stack direction="row" spacing={1}>
              {alerts.map((a) => (
                <Chip
                  key={a.id}
                  label={a.message}
                  icon={<a.icon />}
                  size="small"
                  color={a.type === 'error' ? 'error' : 'warning'}
                  variant="outlined"
                />
              ))}
            </Stack>
          )}
        </Stack>
      </Paper>
    </Box>
  );
}

// Export the colors for other components
export { PMS_COLORS };
